import { useMemo } from "react";
import { Plus, Trash2, Check, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import {
  type BeneficiaryRow,
  type BeneficiaryAllocationValue,
  emptyRow,
  rowComplete,
  sumPct,
  defaultBeneficiaryAllocation,
  isBeneficiaryAllocationValid,
  toInsertBeneficiaries,
  mirrorPrimaryToLegacy,
  fromBeneficiaryRows,
} from "./beneficiaryAllocation.logic";

// Re-export the pure helpers so existing imports from this module keep working.
export type { BeneficiaryRow, BeneficiaryAllocationValue };
export {
  defaultBeneficiaryAllocation,
  isBeneficiaryAllocationValid,
  toInsertBeneficiaries,
  mirrorPrimaryToLegacy,
  fromBeneficiaryRows,
};

/**
 * Multi-beneficiary allocation block for Life / AD&D enrollment.
 *
 * One required tier whose percentages total exactly 100% (max 5 rows).
 * Visuals follow design_handoff_beneficiary_allocation.
 *
 * Fully controlled: the parent owns the value and gates Save on
 * `isBeneficiaryAllocationValid`. Persist with `saveBeneficiaries` and seed
 * from saved rows with `fromBeneficiaryRows`. Rows are written as
 * `beneficiaryType: "primary"` (the DB column still distinguishes
 * primary/contingent for future flexibility — only "primary" is exposed today).
 */

export const BENEFICIARY_RELATIONSHIPS = [
  "Spouse",
  "Child",
  "Parent",
  "Sibling",
  "Domestic partner",
  "Trust",
  "Estate",
  "Other",
] as const;

const MAX_BENEFICIARIES = 5;

/**
 * Persist the full beneficiary set against the base (employee) enrollment row.
 * Wraps the /replace endpoint so callers don't repeat fetch/JSON/error boilerplate.
 * Throws on failure.
 */
export async function saveBeneficiaries(
  enrollmentId: number,
  value: BeneficiaryAllocationValue,
  ctx: { employeeId: number; planId: number },
): Promise<void> {
  const payload = toInsertBeneficiaries(value, { enrollmentId, ...ctx });
  const res = await apiRequest(
    "POST",
    `/api/enrollments/${enrollmentId}/beneficiaries/replace`,
    { beneficiaries: payload },
  );
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message || "Failed to save beneficiaries");
  }
}

// ──────────────────────────────────────────────────────────────────────────

interface TierProps {
  rows: BeneficiaryRow[];
  onChange: (rows: BeneficiaryRow[]) => void;
}

function BeneficiaryList({ rows, onChange }: TierProps) {
  const total = sumPct(rows);
  const remaining = 100 - total;
  const over = total > 100;
  const palette = ["bg-violet-600", "bg-violet-400", "bg-violet-300", "bg-violet-200", "bg-violet-100"];

  const update = (idx: number, next: BeneficiaryRow) =>
    onChange(rows.map((b, i) => (i === idx ? next : b)));
  const add = () => {
    if (rows.length >= MAX_BENEFICIARIES) return;
    onChange([...rows, emptyRow(Math.max(0, remaining))]);
  };
  const remove = (idx: number) => {
    if (rows.length <= 1) return;
    onChange(rows.filter((_, i) => i !== idx));
  };
  const splitEvenly = () => {
    const n = rows.length;
    if (n === 0) return;
    const base = Math.floor(100 / n);
    const rem = 100 - base * n;
    onChange(rows.map((b, i) => ({ ...b, pct: base + (i < rem ? 1 : 0) })));
  };

  const incomplete = rows.length > 0 && total === 100 && !rows.every(rowComplete);

  return (
    <div className="rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-900/40 px-3.5 py-3">
      {/* Header */}
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-xs font-semibold text-stone-900 dark:text-stone-100">Beneficiaries</span>
        <span className="text-[11px] text-stone-400">· must total 100%</span>
        <span className="text-[11px] text-amber-600">*</span>
      </div>

      {/* Allocation bar */}
      <div
        className={cn(
          "relative h-2 rounded-full overflow-hidden bg-violet-100 dark:bg-violet-950/40 border mb-1.5",
          total === 100 ? "border-emerald-300" : over ? "border-red-300" : "border-violet-200",
        )}
      >
        <div className="absolute inset-0 flex">
          {rows.map((b, i) => (
            <div
              key={i}
              className={cn(palette[i % palette.length], i < rows.length - 1 && "border-r-[1.5px] border-white")}
              style={{ width: `${Math.max(0, Math.min(100, Number(b.pct) || 0))}%`, transition: "width 160ms" }}
            />
          ))}
        </div>
        {over && <div className="absolute top-0 bottom-0 right-0 w-1 bg-red-600 shadow-[0_0_0_1.5px_#fff]" />}
      </div>

      {/* Status line */}
      <div className="flex justify-between items-center text-[11px] mb-2.5 font-mono">
        <span className="text-stone-400">
          {rows.length} beneficiar{rows.length === 1 ? "y" : "ies"}
        </span>
        <span
          className={cn(
            "font-semibold inline-flex items-center gap-1",
            total === 100 ? "text-emerald-700" : over ? "text-red-700" : "text-amber-700",
          )}
        >
          {total === 100 && <Check className="h-3 w-3 text-emerald-500" />}
          {over && <AlertTriangle className="h-3 w-3 text-red-600" />}
          {total}% allocated
          {total < 100 && total > 0 && <span className="font-normal text-stone-400">· {remaining}% remaining</span>}
          {over && <span className="font-semibold text-red-600">· over by {total - 100}%</span>}
        </span>
      </div>

      {/* Rows */}
      <div className="flex flex-col gap-1.5">
        {rows.map((b, i) => {
          const pctTooHigh = (Number(b.pct) || 0) > 0 && over;
          return (
            <div
              key={i}
              className={cn(
                "relative grid items-center gap-2.5 rounded-lg border bg-white dark:bg-stone-900 px-3 py-2.5 overflow-hidden",
                pctTooHigh ? "border-red-300" : "border-stone-200 dark:border-stone-700",
              )}
              style={{ gridTemplateColumns: "auto 1fr 1fr 1fr 96px 32px" }}
            >
              <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-violet-600" />
              <span className="ml-1 h-6 w-6 rounded-full inline-flex items-center justify-center text-[10.5px] font-bold font-mono shrink-0 bg-violet-600 text-white">
                {i + 1}
              </span>
              <Input
                value={b.firstName}
                onChange={(e) => update(i, { ...b, firstName: e.target.value })}
                placeholder="First name"
                className="h-8 text-[12.5px]"
              />
              <Input
                value={b.lastName}
                onChange={(e) => update(i, { ...b, lastName: e.target.value })}
                placeholder="Last name"
                className="h-8 text-[12.5px]"
              />
              <Select value={b.relationship || undefined} onValueChange={(v) => update(i, { ...b, relationship: v })}>
                <SelectTrigger className="h-8 text-[12.5px]">
                  <SelectValue placeholder="Relationship…" />
                </SelectTrigger>
                <SelectContent>
                  {BENEFICIARY_RELATIONSHIPS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div
                className={cn(
                  "flex items-center rounded-md border bg-white dark:bg-stone-900 pr-2",
                  pctTooHigh ? "border-red-600" : "border-stone-200 dark:border-stone-700",
                )}
              >
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={b.pct === 0 ? "0" : String(b.pct)}
                  onChange={(e) => {
                    const v = e.target.value;
                    update(i, { ...b, pct: v === "" ? 0 : Math.max(0, Math.min(100, Math.round(Number(v)))) });
                  }}
                  className={cn(
                    "h-8 border-0 text-right font-mono text-[13px] font-semibold focus-visible:ring-0 px-2",
                    pctTooHigh && "text-red-700",
                  )}
                />
                <span className="text-[12px] text-stone-400 font-mono">%</span>
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                disabled={rows.length <= 1}
                title={rows.length <= 1 ? "At least one beneficiary required" : "Remove beneficiary"}
                className={cn(
                  "h-7 w-7 inline-flex items-center justify-center rounded-md",
                  rows.length <= 1
                    ? "text-stone-300 cursor-not-allowed"
                    : "text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800",
                )}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 mt-2.5">
        <Button type="button" variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={add} disabled={rows.length >= MAX_BENEFICIARIES}>
          <Plus className="h-3 w-3" /> Add beneficiary
        </Button>
        {rows.length > 1 && (
          <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={splitEvenly}>
            Split evenly
          </Button>
        )}
        <div className="flex-1" />
        {(total !== 100 || incomplete) && (
          <span className="text-[11px] italic text-stone-400">
            {total !== 100 ? "Allocate exactly 100% to continue" : "Name + relationship required on every row"}
          </span>
        )}
      </div>
    </div>
  );
}

interface BeneficiaryAllocationProps {
  value: BeneficiaryAllocationValue;
  onChange: (value: BeneficiaryAllocationValue) => void;
}

export function BeneficiaryAllocation({ value, onChange }: BeneficiaryAllocationProps) {
  const v = useMemo(() => value ?? defaultBeneficiaryAllocation(), [value]);
  return (
    <BeneficiaryList
      rows={v.primary}
      onChange={(primary) => onChange({ ...v, primary })}
    />
  );
}
